import React, { useState, useContext } from 'react';
import { ConfirmModal } from '@mummssoftware/common-ui';
import { HopeGlobalContext } from '@mummssoftware/global-context';
import { Tooltip, IconButton } from '@material-ui/core';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import AssignmentTurnedInOutlinedIcon from '@material-ui/icons/AssignmentTurnedInOutlined';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import MailIcon from '@material-ui/icons/Mail';
import DownloadIcon from '@material-ui/icons/GetApp';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {
  HUV1Data,
  HUV2Data,
  NFV,
  hopeOptions,
  approve,
  accepted,
  download,
  redownload,
  rejected,
  unapprove,
  unaccept,
  discard,
  getAvailableMilestones,
  isPreVisitWaitingStatus,
} from '@mummssoftware/utils';
import { InProgress } from '@mummssoftware/utils/constants';
import styled from 'styled-components';
import { ICellRendererParams } from 'ag-grid-community';
import { SFVContent } from './SFVContent';
import { HopeContext } from '../Context/HopeWidgetContext';
import { Context } from '../../context';
import { SentandAccepted, Complete } from '../HopeReviewDocument/constants';
import { SET_SHOW_LOADER, SET_SHOW_DETAIL } from '../Context/constants';
import {
  rowIsToday,
  cellHasActions,
  eventHasExtraData,
  getHopeCalendarDayOfService,
} from '../Context/utils';
import defaults from '../../translations/en.json';
const approveDirectly = 'approveDirectly';
const approveAndDownload = 'approveAndDownload';

// to make sure we do not miss any milestones
const defaultVisitRole = {
  careProviderRoleGroup: 'Nurse',
  careProviderRole: 'RN',
};

const getIcon = (
  actions: mumms.HopeAction[],
  status: string,
  isDischargedPending: boolean,
) => {
  const color =
    isDischargedPending || actions.find((a: mumms.HopeAction) => !a.disabled)
      ? undefined
      : 'disabled';
  if (
    status.toLowerCase().includes(InProgress.toLowerCase()) ||
    isPreVisitWaitingStatus(status)
  ) {
    return <></>;
  } else if (actions.find((a: mumms.HopeAction) => a.actn === approve)) {
    return <AssignmentTurnedInOutlinedIcon color={color} />;
  } else if (actions.find((a: mumms.HopeAction) => a.actn === download)) {
    return <DownloadIcon color={color} />;
  } else if (actions.find((a: mumms.HopeAction) => a.actn === accepted)) {
    return <MailIcon color={color} />;
  } else if (
    status === SentandAccepted ||
    actions.find((a: mumms.HopeAction) => a.actn === unaccept)
  ) {
    return <RemoveCircleOutlineIcon color={color} />;
  }

  return <AssignmentOutlinedIcon color={color} />;
};

type customCellProps = {
  colDef: { field: string };
};

type HopeCellRendererProps = ICellRendererParams & customCellProps;

const computeIsEnabled = (
  data: mumms.HopeEvent,
  node: any,
  patientHopeDetail: mumms.HopeEvent[] | null,
  ptDischargeddate: string | null,
  hasEventActions: boolean,
) =>
  data?.summary ||
  rowIsToday(node, patientHopeDetail) ||
  (!!ptDischargeddate && hasEventActions);

const computeAllowActions = ({
  data,
  field,
  node,
  patientHopeDetail,
  ptDischargeddate,
  ptAdmitdate,
  hasEventActions,
}: {
  data: mumms.HopeEvent;
  field: string;
  node: any;
  patientHopeDetail: mumms.HopeEvent[] | null;
  ptDischargeddate: string | null;
  ptAdmitdate: string | null;
  hasEventActions: boolean;
}) => {
  let allowActions = false;
  if (!eventHasExtraData(data[field], field) && hasEventActions) {
    if (rowIsToday(node, patientHopeDetail) || data.summary) {
      allowActions = true;
    } else if (ptDischargeddate && hasEventActions) {
      const dos = getHopeCalendarDayOfService(ptAdmitdate, ptDischargeddate);
      if (dos == null) {
        allowActions = false;
      } else if (data[field].dos === dos) {
        const milestone = getAvailableMilestones(data, defaultVisitRole);

        const mostRecentMilestone =
          patientHopeDetail &&
          patientHopeDetail.length &&
          patientHopeDetail[patientHopeDetail.length - 1];
        const mostRecentMilestoneStatus =
          mostRecentMilestone && mostRecentMilestone[field]
            ? mostRecentMilestone[field].status
            : null;

        if (
          milestone.milestone &&
          milestone.status &&
          milestone.key === field &&
          !mostRecentMilestoneStatus
        ) {
          allowActions = true;
        }
      }
    }
  }
  return allowActions;
};

export const CellDiv = styled.div`
  display: flex;
  justify-content: center;
  min-width: 30px;
`;

export const ButtonCellRenderer = (props: HopeCellRendererProps) => {
  const {
    getPatientHopeReviewItems,
    dispatch,
    DownloadUnsentXML,
    updateHopeWidgetStatus,
    approveAndDownloadXML,
    batchMode,
  } = useContext(HopeContext);

  const { readOnly, patient } = useContext(Context);
  const { refuseSIAVisit, patientHopeDetail } = useContext(HopeGlobalContext);
  const {
    data,
    colDef: { field },
    node,
  } = props;
  const ptDischargeddate =
    patient?.dischargeDate || data?.dischargedate_shifted || null;
  const ptAdmitdate =
    patient?.admissionDate || data?.admissiondate_shifted || null;

  const buttonStyle: any = {
    height: '7px',
    width: '7px',
    margin: '-3px',
    color: 'grey',
  };
  const eventData = data[field];
  const hasEventActions = cellHasActions(eventData);
  const hasDiscard =
    hasEventActions &&
    !!eventData.possibleactions?.find(
      (a: mumms.HopeAction) => a.actn === discard,
    );
  const isEnabled = computeIsEnabled(
    data,
    node,
    patientHopeDetail,
    ptDischargeddate,
    hasEventActions,
  );
  const allowActions = computeAllowActions({
    data,
    field,
    node,
    patientHopeDetail,
    ptDischargeddate,
    ptAdmitdate,
    hasEventActions,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [modalOption, setModalOption] = useState<string | null>(null);
  const [confirmationValue, setConfirmationValue] = useState<string | null>(
    null,
  );
  const [buttonBlocked, setButtonBlocked] = useState(false);

  const statusMessage = eventData.statusmessage;

  // Waiting on visit: no clipboard / action menu (Complete NVR/SIA not usable yet).
  if (!allowActions || batchMode || isPreVisitWaitingStatus(statusMessage)) {
    return <div></div>;
  }

  const toolTipTitle =
    eventData.possibleactions?.length &&
    eventData.possibleactions
      .map((action: mumms.HopeAction) => hopeOptions[action.actn])
      .join(', ');

  const disableSubmit = confirmationValue === null;
  const allowedActions = eventData.possibleactions;

  const resetUI = () => {
    setAnchorEl(null);
    setModalOption(null);
    setConfirmationValue(null);
  };

  const onCancelClick = () => {
    resetUI();
  };

  const onClickSubmit = () => {
    dispatch({ type: SET_SHOW_LOADER, payload: true });
    refuseSIAVisit(confirmationValue, {
      ...eventData,
      id: data.patientId,
      episodeNum: data.episodenum,
      patientNumber: data.patientnumber,
    }).then((res: any) => {
      if (!(res instanceof Response)) {
        updateHopeWidgetStatus(
          data.patientId,
          data.episodenum,
          eventData.milestone,
          NFV,
        ).then(() => {
          resetUI();
        });
      }
    });
  };

  const handleAction = (option: string) => {
    switch (option) {
      case NFV:
        setModalOption(option);
        break;
      case approve:
        dispatch({ type: SET_SHOW_LOADER, payload: true });
        getPatientHopeReviewItems(
          data.patientnumber,
          data.episodenum,
          eventData.milestone,
        ).then((res: any) => {
          if (!(res instanceof Response)) {
            dispatch({ type: SET_SHOW_DETAIL, payload: data });
          }
        });
        break;
      case download:
      case redownload:
        setButtonBlocked(true);
        dispatch({ type: SET_SHOW_LOADER, payload: true });
        DownloadUnsentXML(
          data.patientId,
          data.episodenum,
          eventData.milestone,
        ).then((res: any) => {
          if (!(res instanceof Response) && option === download) {
            updateHopeWidgetStatus(
              data.patientId,
              data.episodenum,
              eventData.milestone,
              option,
            );
          }
          setButtonBlocked(false);
        });
        break;
      case approveDirectly:
        setButtonBlocked(true);
        dispatch({ type: SET_SHOW_LOADER, payload: true });
        updateHopeWidgetStatus(
          data.patientId,
          data.episodenum,
          eventData.milestone,
          approve,
        ).then(() => {
          setButtonBlocked(false);
        });
        break;
      case approveAndDownload:
        setButtonBlocked(true);
        approveAndDownloadXML(
          data.patientId,
          data.episodenum,
          eventData.milestone,
        ).then(() => {
          setButtonBlocked(false);
        });
        break;
      case accepted:
      case rejected:
      case unaccept:
      case unapprove:
        setButtonBlocked(true);
        dispatch({ type: SET_SHOW_LOADER, payload: true });
        updateHopeWidgetStatus(
          data.patientId,
          data.episodenum,
          eventData.milestone,
          option,
        );
        break;
      case discard: {
        setButtonBlocked(true);
        dispatch({ type: SET_SHOW_LOADER, payload: true });
        const milestoneForDiscard =
          eventData.milestone || (field === HUV1Data ? 'HUV1' : 'HUV2');
        updateHopeWidgetStatus(
          data.patientId,
          data.episodenum,
          milestoneForDiscard,
          discard,
        ).then(() => setButtonBlocked(false));
        break;
      }
      default:
        break;
    }
  };

  const handleClick = (event: any) => {
    // stop propagation of panel expansion
    event.stopPropagation();
    (isEnabled || hasDiscard) && setAnchorEl(event.currentTarget);
  };
  buttonStyle.color = hasDiscard ? '#ff9800' : undefined;

  const getMenuUI = () =>
    modalOption ? (
      <ConfirmModal
        onOKClick={onClickSubmit}
        onCancelClick={onCancelClick}
        isOkDisabled={disableSubmit}
        okText={defaults.submit}
        noText={defaults.cancel}
        modalText={hopeOptions[modalOption]}
        content={
          <>
            <SFVContent
              handleChange={setConfirmationValue}
              value={confirmationValue}
            />
          </>
        }
      />
    ) : (
      <>
        {allowedActions &&
          allowedActions.map((option: mumms.HopeAction) => {
            if (option.actn === approve) {
              const hasNoErrors =
                !eventData.numerrors || eventData.numerrors === 0;
              return (
                <React.Fragment key={option.actn}>
                  <MenuItem
                    onClick={() => handleAction(approve)}
                    id="hope_actions"
                    disabled={readOnly || option.disabled || buttonBlocked}
                  >
                    {hopeOptions[approve]}
                  </MenuItem>
                  {hasNoErrors && (
                    <MenuItem
                      onClick={() => handleAction(approveDirectly)}
                      id="hope_actions"
                      disabled={readOnly || option.disabled || buttonBlocked}
                    >
                      {defaults.Approve}
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => handleAction(approveAndDownload)}
                    id="hope_actions"
                    disabled={readOnly || option.disabled || buttonBlocked}
                  >
                    {defaults.ApproveAndDownload}
                  </MenuItem>
                </React.Fragment>
              );
            }
            return (
              <MenuItem
                key={option.actn}
                onClick={() => {
                  handleAction(option.actn);
                }}
                id="hope_actions"
                disabled={readOnly || option.disabled || buttonBlocked}
              >
                {hopeOptions[option.actn]}
              </MenuItem>
            );
          })}
      </>
    );
  return (
    <>
      <Tooltip
        title={toolTipTitle}
        disableHoverListener={isEnabled || hasDiscard}
      >
        <IconButton
          style={buttonStyle}
          size="small"
          color="primary"
          onClick={handleClick}
        >
          {getIcon(allowedActions ?? [], statusMessage, hasDiscard)}
        </IconButton>
      </Tooltip>
      <React.Fragment>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={onCancelClick}
        >
          <div>{getMenuUI()}</div>
        </Menu>
      </React.Fragment>
    </>
  );
};

const getCellStyle = (style: any, props: any, palette: any) => {
  const {
    colDef: { field, displayStatus },
    data,
  } = props;
  const { possibleactions, numwarnings, numerrors, status } = data[
    field
  ] as mumms.HopeMilestoneEvent;
  const { summary } = data;
  // text color
  // if there are actions due today or in the past => red
  const statusNoStyle = status !== SentandAccepted && status !== Complete;
  const noDue =
    !!possibleactions && !!possibleactions[0] && possibleactions[0]?.due <= 0;
  if (statusNoStyle && noDue) {
    style.color = palette.error.main;
  }
  // background color
  // if null then cell is before event starts => light grey
  // if actions is an empty array and there is no status message => green
  if (
    (field === HUV1Data && data?.dayofservice <= 5) ||
    (field === HUV2Data && data?.dayofservice <= 15) ||
    data[field]?.statusmessage === null
  ) {
    style.backgroundColor = 'lightgrey';
  } else if (possibleactions?.length === 0 && !status) {
    style.backgroundColor = palette.success.light;
  }
  if (displayStatus && summary) {
    if (numerrors > 0) {
      style.border = 'solid red 2px';
    } else if (numwarnings > 0) {
      style.border = 'solid yellow 2px';
    }
  }
  return style;
};

export const getNumberCellStyle = (props: any, palette: any) => {
  const style: any = { 'justify-content': 'end' };
  return getCellStyle(style, props, palette);
};

export const getTextCellStyle = (props: any, palette: any) => {
  const style: any = {
    'justify-content': 'start',
    display: 'inline-block',
    position: 'absolute',
    overflow: 'ellipsis',
  };
  return getCellStyle(style, props, palette);
};

export const getButtonCellStyle = (
  props: any,
  _palette: any,
  _patient?: mumms.patient,
) => {
  const {
    colDef: { field },
    data,
  } = props;
  const style: any = {};
  // Match pre-event lightgrey on status/time/due so the action column
  // does not leave a zebra "cube" at the start of the grey band.
  if (
    (field === HUV1Data && data?.dayofservice <= 5) ||
    (field === HUV2Data && data?.dayofservice <= 15) ||
    data[field]?.statusmessage === null
  ) {
    style.backgroundColor = 'lightgrey';
  }
  return style;
};
